import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "reactstrap";
import {
    usePagination,
    useTable,
    useRowSelect,
    useGlobalFilter,
} from "react-table";
import BTable from "react-bootstrap/Table";
import { useSnapshot } from "valtio";
import { StyledButton } from "@aureskonnect/react-ui";

import { store as vStore } from "@vStore";
import store, {
    removeSalesSupportItem,
} from "@pages/Statistics/Transactions/store";

import { capitalize } from "@helpers/general";

import { ITEMS_PER_PAGE } from "@constants/index";

import Pagination from "@components/Common/Table/Pagination";
import { ArrowLeftSvgIcon } from "@components/Common/SvgIcons";
import { Styles } from "../Styles";

type PropsType = {
    data: any[];
    previous: any;
};

export default function MySelection({ data, previous }: PropsType) {
    const { t } = useTranslation();

    const { mainColor } = useSnapshot(vStore);
    const advancedStore = useSnapshot(store);

    const [localData, setLocalData] = React.useState(data);

    function handleRemoveMultipleSelectedSalesSupport() {
        let localSelectedSalesSupport: any[] = [
            ...selectedFlatRows.map((row: any) => row.original),
        ];

        selectedFlatRows
            .map((row: any) => row.original)
            .forEach((item: string) => {
                removeSalesSupportItem(item);
            });

        setLocalData(
            localData.filter((salesSupport: any) => {
                return !localSelectedSalesSupport
                    .map((s) => s.toLowerCase())
                    .includes(salesSupport.toLowerCase());
            })
        );
    }

    const handleRemoveSelectedSalesSupport = React.useCallback(
        (salesSupport: any) => {
            setLocalData(
                localData.filter((datum: string) => datum !== salesSupport)
            );
            removeSalesSupportItem(salesSupport);
        },
        [localData]
    );

    const columns: AdvancedTableColumnsType[] = React.useMemo(
        () => [
            {
                Header: t("Designation"),
                accessor: (salesSupport: string): string =>
                    t(capitalize(salesSupport)),
            },
            {
                Header: "",
                id: "actions",
                disableGlobalFilter: true,
                accessor: function (element: any) {
                    return (
                        <div>
                            <Button
                                type="button"
                                outline={true}
                                block={true}
                                color={mainColor === null ? "secondary" : ""}
                                className="d-none d-sm-none d-md-block p-1 white-space-nowrap"
                                onClick={() =>
                                    handleRemoveSelectedSalesSupport(element)
                                }
                                style={{
                                    width: "150px",
                                    float: "right",
                                    color: mainColor,
                                    borderColor: mainColor,
                                }}
                            >
                                {t("Delete")}
                            </Button>
                            <button
                                className="border-0 d-block d-sm-block d-md-none p-1 bg-transparent__clz icon-color__clz"
                                onClick={() =>
                                    handleRemoveSelectedSalesSupport(element)
                                }
                            >
                                <i className="bx bxs-trash-alt font-size-24"></i>
                            </button>
                        </div>
                    );
                },
            },
        ],
        [t, handleRemoveSelectedSalesSupport, mainColor]
    );

    const IndeterminateCheckbox = React.forwardRef(
        ({ indeterminate, ...rest }: any, ref: any) => {
            const defaultRef = React.useRef();
            const resolvedRef = ref || defaultRef;

            React.useEffect(() => {
                resolvedRef.current!.indeterminate = indeterminate;
            }, [resolvedRef, indeterminate]);

            let localProps = { ...rest, title: t(rest.title) };

            return <input type="checkbox" ref={resolvedRef} {...localProps} />;
        }
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex },
        setGlobalFilter,
        selectedFlatRows,
    } = useTable(
        {
            columns,
            data: localData,
            initialState: { pageIndex: 0 },
        },
        useGlobalFilter,
        usePagination,
        useRowSelect,
        (hooks) => {
            hooks.visibleColumns.push((columns) => [
                {
                    id: "selection",
                    Header: ({ getToggleAllRowsSelectedProps }) => (
                        <IndeterminateCheckbox
                            {...getToggleAllRowsSelectedProps()}
                        />
                    ),
                    Cell: ({ row }) => (
                        <IndeterminateCheckbox
                            {...row.getToggleRowSelectedProps()}
                        />
                    ),
                    disableGlobalFilter: true,
                },
                ...columns,
            ]);
        }
    );

    React.useEffect(() => {
        setPageSize(ITEMS_PER_PAGE);
    }, [setPageSize]);

    return (
        <React.Fragment>
            <div className="d-flex justify-content-between">
                <div className="d-flex align-items-center">
                    <ArrowLeftSvgIcon
                        style={{
                            cursor: "pointer",
                        }}
                        className="ml-2"
                        onClick={() => previous()}
                    />
                    <h5 className="ml-3 mb-0 white-space-nowrap">{`${t(
                        "My selection"
                    )} (${data.length})`}</h5>
                    <div className="app-search m-2 p-0">
                        <div className="position-relative">
                            <input
                                type="text"
                                className="form-control w-xs-90"
                                placeholder={`${t("Search")} ...`}
                                onChange={(e) => {
                                    setGlobalFilter(e.target.value);
                                }}
                            />
                            <span className="bx bx-search-alt"></span>
                        </div>
                    </div>
                </div>
                <StyledButton
                    rounded={true}
                    outline={false}
                    variant={mainColor === null ? "secondary" : ""}
                    className="m-2 white-space-nowrap"
                    style={{
                        background: mainColor,
                    }}
                    disabled={selectedFlatRows.length === 0}
                    onClick={handleRemoveMultipleSelectedSalesSupport}
                >
                    {t("Delete")}
                </StyledButton>
            </div>
            <Styles>
                <BTable responsive {...getTableProps()}>
                    <thead>
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column) => {
                                    return (
                                        <th {...column.getHeaderProps()}>
                                            {column.render("Header")}
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {page.map((row, i) => {
                            prepareRow(row);
                            return (
                                <tr {...row.getRowProps()}>
                                    {row.cells.map((cell) => {
                                        return (
                                            <td
                                                className="white-space-nowrap"
                                                {...cell.getCellProps()}
                                            >
                                                {cell.render("Cell")}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </BTable>
            </Styles>
            {advancedStore.salesModeSelection.length > ITEMS_PER_PAGE ? (
                <Pagination
                    canPreviousPage={canPreviousPage}
                    canNextPage={canNextPage}
                    pageOptions={pageOptions}
                    pageCount={pageCount}
                    gotoPage={gotoPage}
                    nextPage={nextPage}
                    previousPage={previousPage}
                    pageIndex={pageIndex}
                />
            ) : null}
        </React.Fragment>
    );
}
